import type { RelatedApp } from '@wix/add-panel-common';
import classnames from 'classnames';
import React, { FC, MouseEvent, useCallback } from 'react';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import { Button } from '@wix/wix-base-ui';
import { ADD_TO_SITE_TRANSLATION_KEY } from '../../../../../constants/translationKeys';
import { useActionsDispatcher } from '../../../../ActionsDispatcher';
import { useCurrentCategory } from '../../../../CurrentCategoryProvider';
import { useRelatedApps } from '../../../../RelatedAppsProvider';
import { useUtils } from '../../../../UtilsProvider';
import { ActionItem } from '../components/ActionItem';
import { Icon } from './Icon';
import { getSortedCategoryApps } from './relatedAppsUtils';
import type { RelatedAppsProps } from './RelatedApps.types';
import styles from './RelatedApps.scss';

const DEFAULT_DRAG_AND_DROP_WIDTH = 980;
const DEFAULT_DRAG_AND_DROP_HEIGHT = 500;

export const RelatedApps: FC<RelatedAppsProps> = ({ section }) => {
  const { dispatchDragRelatedApp, dispatchAddRelatedApp, dispatchOpenWixAppMarket } = useActionsDispatcher();

  const {
    translations: { title: sectionTitle, stillHaventFound, browseAppMarket },
    props,
  } = section;
  const { relatedApps } = useRelatedApps();
  const category = useCurrentCategory();
  const { id: categoryId } = category;

  const { translate } = useUtils();

  const handleMouseDown = (event: MouseEvent, app: RelatedApp) => {
    const element = event.currentTarget;
    const boundingRect = element.getBoundingClientRect();

    const rect = {
      left: boundingRect.left,
      top: boundingRect.top,
      width: 60,
      height: 60,
    };

    const [firstWidget] = app.widgets || [];
    const layout =
      app.hasSection || !firstWidget
        ? {
            width: DEFAULT_DRAG_AND_DROP_WIDTH,
            height: DEFAULT_DRAG_AND_DROP_HEIGHT,
          }
        : {
            width: firstWidget.defaultWidth,
            height: firstWidget.defaultHeight,
          };

    dispatchDragRelatedApp({
      event,
      categoryId,
      sectionTitle,
      app,
      dragItemData: {
        rect,
        appItem: {
          structure: {
            layout,
          },
        },
      },
    });
  };

  const handleOpenWixAppMarketClick = useCallback(() => {
    dispatchOpenWixAppMarket({
      categoryId,
      referralInfo: '',
    });
  }, [categoryId, dispatchOpenWixAppMarket]);

  if (!props.categoryNum && !props.tagId) {
    return null;
  }

  const categoryRelatedApps = getSortedCategoryApps(relatedApps, props.categoryNum, props.tagId);

  const translatedTooltip = translate(ADD_TO_SITE_TRANSLATION_KEY);

  return (
    <div>
      <ul>
        {categoryRelatedApps.map((app) => (
          <li key={app.appDefinitionId}>
            <ActionItem
              dataHook={app.automationId}
              title={app.name}
              description={app.teaser}
              className={styles.actionItem}
              illustration={
                <Icon
                  imageUrl={`url(${app.appIcon})`}
                  onMouseDown={(event) => handleMouseDown(event, app)}
                  dataHook="related-app-icon"
                />
              }
              actionIcon={<Symbol name="plusBig" />}
              tooltipContent={translatedTooltip}
              onClick={() => dispatchAddRelatedApp({ app, categoryId })}
              shouldTranslate={false}
            />
          </li>
        ))}
      </ul>
      <div className={styles.more}>
        <div className={styles.stillHaventFoundTextWrapper}>
          <Symbol name="market" />
          <span className={styles.stillHaventFoundText}>{translate(stillHaventFound)}</span>
        </div>
        <Button
          className={classnames('btn-text', styles.openWixAppMarketLink)}
          dataHook="open-wix-app-market-link"
          onClick={handleOpenWixAppMarketClick}
        >
          {translate(browseAppMarket)}
        </Button>
      </div>
    </div>
  );
};
